import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { 
  FaHome, FaCar, FaUsers, FaClipboardList, FaGolfBall, FaTrailer,
  FaChartBar, FaCog, FaFileContract, FaHandshake, FaCreditCard,
  FaCalendarAlt, FaComments, FaBlog, FaRobot, FaCalculator,
  FaShoppingCart, FaBell, FaBuilding, FaFileAlt, FaUserShield,
  FaWrench, FaEnvelope, FaMoneyBillWave, FaClipboardCheck,
  FaPhoneAlt, FaTruck, FaFileInvoiceDollar, FaUserTie,
  FaIdCard, FaSearch, FaBalanceScale, FaChartLine, FaBox,
  FaTools, FaDollarSign, FaCommentDots, FaMoon, FaSun,
  FaUser, FaCircle, FaSignOutAlt, FaExpand, FaCompress,
  FaShieldAlt, FaRss
} from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import { FiChevronDown, FiX, FiMenu } from 'react-icons/fi';

const AdminNavigationModern = ({ activeTab, setActiveTab }) => {
  const { signOut, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState(0);
  const [darkMode, setDarkMode] = useState(() => {
    // Load dark mode preference from localStorage
    const saved = localStorage.getItem('adminDarkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [fullscreen, setFullscreen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [expandedDropdown, setExpandedDropdown] = useState(null);
  const [clickedDropdown, setClickedDropdown] = useState(null);

  // Apply dark mode class to body
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('adminDarkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Clock
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const navItems = [
    { 
      name: 'Dashboard', 
      icon: FaHome, 
      component: 'dashboard',
      color: 'from-blue-600/80 to-blue-700/80',
      dropdown: [
        { name: 'Overview', component: 'dashboard', icon: FaHome },
        { name: 'Analytics', component: 'analytics', icon: FaChartBar }
      ]
    },
    { 
      name: 'Inventory', 
      icon: FaBox,
      component: 'unit-type',
      color: 'from-blue-600/80 to-blue-700/80',
      dropdown: [
        { name: 'Vehicles', component: 'vehicles', icon: FaCar },
        { name: 'Golf Carts', component: 'golf-carts', icon: FaCar },
        { name: 'Trailers', component: 'trailers', icon: FaCar },
        { name: 'Boats', component: 'boats', icon: FaCar }
      ]
    },
    { 
      name: 'Deals', 
      icon: FaFileContract,
      component: 'deals',
      color: 'from-blue-600/80 to-blue-700/80',
      dropdown: [
        { name: 'Deal Management', component: 'deals', icon: FaFileContract },
        { name: 'Deal Builder', component: 'deal-builder', icon: HiSparkles },
        { name: 'Credit Applications', component: 'credit-apps', icon: FaFileContract },
        { name: 'Pull Credit Report', component: 'credit-check', icon: FaCreditCard },
        { name: 'Contracts', component: 'contracts', icon: FaFileContract },
        { name: 'Forms', component: 'forms', icon: FaFileContract }
      ]
    },
    { 
      name: 'Service', 
      icon: FaTools, 
      component: 'service',
      color: 'from-blue-600/80 to-blue-700/80'
    },
    { 
      name: 'Leads', 
      icon: FaUsers, 
      component: 'leads',
      color: 'from-blue-600/80 to-blue-700/80'
    },
    { 
      name: 'Payments', 
      icon: FaDollarSign, 
      component: 'payments',
      color: 'from-blue-600/80 to-blue-700/80',
      dropdown: [
        { name: 'Payment Management', component: 'payments', icon: FaCreditCard },
        { name: 'Recurring Payments', component: 'subscriptions', icon: FaCalendarAlt },
        { name: 'Test Authorize.Net', component: 'payment-test', icon: FaShieldAlt }
      ]
    },
    { 
      name: 'Messages', 
      icon: FaCommentDots, 
      component: 'sms',
      color: 'from-blue-600/80 to-blue-700/80'
    },
    { 
      name: 'AI Chat', 
      icon: FaRobot, 
      component: 'ai-chat',
      color: 'from-blue-600/80 to-blue-700/80'
    },
    { 
      name: 'CarGurus', 
      icon: FaChartLine, 
      component: 'cargurus',
      color: 'from-blue-600/80 to-blue-700/80'
    },
    { 
      name: 'VIN Decoder', 
      icon: FaSearch, 
      component: 'vin-decoder',
      color: 'from-blue-600/80 to-blue-700/80'
    },
    { 
      name: 'Google VLA Feed', 
      icon: FaRss, 
      component: 'google-vla',
      color: 'from-orange-500/80 to-amber-600/80'
    },
    { 
      name: 'Settings', 
      icon: FaCog, 
      component: 'settings',
      color: 'from-blue-600/80 to-blue-700/80'
    }
  ];

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullscreen(true);
    } else {
      document.exitFullscreen();
      setFullscreen(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  // Filter nav items based on search
  const filteredNavItems = navItems.filter(item => 
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (item.dropdown && item.dropdown.some(sub => 
      sub.name.toLowerCase().includes(searchQuery.toLowerCase())
    ))
  );

  return (
    <>
      {/* Floating Admin Panel Indicator */}
      <motion.div
        initial={{ opacity: 0, x: -100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        className="fixed bottom-4 left-4 z-40"
      >
        <div className={`px-3 py-2 rounded-lg backdrop-blur-md shadow-lg ${
          darkMode 
            ? 'bg-slate-800/80 text-gray-200 border border-slate-700/50' 
            : 'bg-white/80 text-gray-700 border border-gray-200/50'
        }`}>
          <p className="text-xs font-medium" style={{ fontSize: '0.65rem' }}>
            Admin Panel
          </p>
        </div>
      </motion.div>

      {/* Dark Mode Styles */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .dark-mode {
          background: #0f172a;
          color: #e2e8f0;
        }
        .dark-mode .dark-bg {
          background: linear-gradient(to right, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95));
        }
        .dark-mode .dark-surface {
          background: rgba(30, 41, 59, 0.95);
        }
        .dark-mode .dark-text {
          color: #e2e8f0;
        }
        .dark-mode .dark-text-muted {
          color: #94a3b8;
        }
        .dark-mode .dark-border {
          border-color: rgba(51, 65, 85, 0.5);
        }
        .dark-mode .dark-hover:hover {
          background: rgba(51, 65, 85, 0.5);
        }
        .dark-mode input, .dark-mode select, .dark-mode textarea {
          background: rgba(30, 41, 59, 0.8);
          color: #e2e8f0;
          border-color: rgba(51, 65, 85, 0.5);
        }
        .dark-mode input::placeholder {
          color: #64748b;
        }
      `}</style>

      {/* Main Navigation Bar - Glassmorphic */}
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50"
      >
        <div className={`${darkMode 
          ? 'dark-bg backdrop-blur-xl border-b dark-border shadow-2xl'
          : 'bg-gradient-to-r from-white/80 via-white/70 to-white/80 backdrop-blur-xl border-b border-white/20 shadow-xl'}`}>
          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <div className="px-6 py-3">
              <div className="flex items-center justify-between">
                {/* Logo Section with Animation */}
                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center space-x-3"
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-primary-400 blur-lg opacity-50"></div>
                      <img
                        src="/assets/assets/images/logo.webp"
                        alt="Paradise-Performance"
                        className="h-12 relative z-10"
                      />
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'dark-text-muted' : 'text-gray-500'}`}>
                        {currentTime.toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Center Navigation Items */}
                <nav className="flex items-center space-x-1 overflow-visible scrollbar-hide max-w-[calc(100vw-280px)]">
                  {filteredNavItems.map((item, index) => (
                    <div 
                      key={item.component}
                      className="relative"
                      onMouseEnter={() => {
                        setHoveredItem(item.component);
                        if (item.dropdown) setExpandedDropdown(item.component);
                      }}
                      onMouseLeave={() => {
                        setHoveredItem(null);
                        if (clickedDropdown !== item.component) setExpandedDropdown(null);
                      }}
                    >
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          if (item.dropdown) {
                            const isOpen = expandedDropdown === item.component;
                            setExpandedDropdown(isOpen ? null : item.component);
                            setClickedDropdown(isOpen ? null : item.component);
                          } else {
                            setActiveTab(item.component);
                            setClickedDropdown(null);
                          }
                        }}
                        title={item.name}
                        className={`
                          relative px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap
                          transition-all duration-300 group
                          ${activeTab === item.component || (item.dropdown && item.dropdown.some(d => d.component === activeTab))
                            ? 'text-white' 
                            : darkMode ? 'dark-text hover:text-gray-100' : 'text-gray-700 hover:text-gray-900'
                          }
                        `}
                      >
                        {/* Active/Hover Background */}
                        <AnimatePresence>
                          {(activeTab === item.component || hoveredItem === item.component || 
                            (item.dropdown && item.dropdown.some(d => d.component === activeTab))) && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              className={`
                                absolute inset-0 rounded-xl bg-gradient-to-r ${item.color}
                                ${activeTab === item.component || (item.dropdown && item.dropdown.some(d => d.component === activeTab))
                                  ? 'opacity-100' 
                                  : 'opacity-80'
                                }
                              `}
                            />
                          )}
                        </AnimatePresence>

                        {/* Content */}
                        <span className="relative z-10 flex items-center space-x-2">
                          <item.icon className="w-4 h-4" />
                          <span>{item.name}</span>
                          {item.dropdown && (
                            <motion.div
                              animate={{ rotate: expandedDropdown === item.component ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <FiChevronDown className="w-3 h-3" />
                            </motion.div>
                          )}
                        </span>
                      </motion.button>

                      {/* Dropdown Menu - Glassmorphic */}
                      <AnimatePresence>
                        {item.dropdown && expandedDropdown === item.component && (
                          <motion.div
                            initial={{ opacity: 0, y: -10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 mt-2 w-56 rounded-2xl overflow-hidden z-[9999]"
                          >
                            <div className={`${darkMode 
                              ? 'dark-surface backdrop-blur-xl border dark-border shadow-2xl rounded-2xl overflow-hidden'
                              : 'bg-white/90 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl overflow-hidden'}`}>
                              {item.dropdown.map((dropItem, idx) => {
                                const IconComponent = dropItem.icon;
                                return (
                                  <motion.button
                                    key={dropItem.component}
                                    whileHover={{ x: 5 }}
                                    onClick={() => {
                                      setActiveTab(dropItem.component);
                                      setExpandedDropdown(null);
                                      setClickedDropdown(null);
                                    }}
                                    className={`
                                      w-full text-left px-4 py-3 flex items-center justify-between
                                      transition-all duration-200
                                      ${activeTab === dropItem.component 
                                      ? 'bg-gradient-to-r ' + item.color + ' text-white' 
                                      : darkMode ? 'dark-hover dark-text' : 'hover:bg-gray-50 text-gray-700'
                                    }
                                    ${idx !== item.dropdown.length - 1 ? darkMode ? 'border-b dark-border' : 'border-b border-gray-100' : ''}
                                    `}
                                  >
                                    <span className="flex items-center space-x-3">
                                      {IconComponent && <IconComponent className="w-4 h-4" />}
                                      <span className="font-medium">{dropItem.name}</span>
                                    </span>
                                    {dropItem.count && (
                                      <span className="text-xs px-2 py-1 rounded-full bg-white/20">
                                        {dropItem.count}
                                      </span>
                                    )}
                                  </motion.button>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </nav>

                {/* Right Section - User Actions */}
                <div className="flex items-center space-x-3">
                  {/* Search */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSearchOpen(!searchOpen)}
                    className={`p-2.5 rounded-xl backdrop-blur transition-all ${darkMode 
                      ? 'bg-white/10 hover:bg-white/20'
                      : 'bg-white/50 hover:bg-white/70'}`}
                  >
                    <FaSearch className={`w-4 h-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                  </motion.button>

                  {/* Notifications */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`relative p-2.5 rounded-xl backdrop-blur transition-all ${darkMode 
                      ? 'bg-white/10 hover:bg-white/20'
                      : 'bg-white/50 hover:bg-white/70'}`}
                  >
                    <FaBell className={`w-4 h-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                    {notifications > 0 && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center"
                      >
                        {notifications}
                      </motion.span>
                    )}
                  </motion.button>

                  {/* Dark Mode Toggle */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setDarkMode(!darkMode)}
                    className={`p-2.5 rounded-xl backdrop-blur transition-all ${darkMode 
                      ? 'bg-white/10 hover:bg-white/20'
                      : 'bg-white/50 hover:bg-white/70'}`}
                  >
                    {darkMode ? 
                      <FaSun className="w-4 h-4 text-yellow-400" /> : 
                      <FaMoon className="w-4 h-4 text-gray-600" />
                    }
                  </motion.button>

                  {/* Fullscreen Toggle */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={toggleFullscreen}
                    className={`p-2.5 rounded-xl backdrop-blur transition-all ${darkMode 
                      ? 'bg-white/10 hover:bg-white/20'
                      : 'bg-white/50 hover:bg-white/70'}`}
                  >
                    {fullscreen ? 
                      <FaCompress className={`w-4 h-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} /> : 
                      <FaExpand className={`w-4 h-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                    }
                  </motion.button>

                  {/* User Profile */}
                  <div className="relative">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setProfileOpen(!profileOpen)}
                      className={`flex items-center space-x-3 p-2 pr-4 rounded-xl backdrop-blur transition-all ${
                        darkMode
                          ? 'bg-gradient-to-r from-primary-500/20 to-primary-600/20 hover:from-primary-500/30 hover:to-primary-600/30'
                          : 'bg-gradient-to-r from-primary-500/10 to-primary-600/10 hover:from-primary-500/20 hover:to-primary-600/20'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center">
                        <FaUser className="w-4 h-4 text-white" />
                      </div>
                      <div className="text-left">
                        <p className={`text-xs font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          {user?.email?.split('@')[0] || 'Admin'}
                        </p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center`}>
                          <FaCircle className="w-2 h-2 text-green-500 mr-1" />
                          Online
                        </p>
                      </div>
                    </motion.button>

                    {/* Profile Dropdown */}
                    <AnimatePresence>
                      {profileOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          className="absolute right-0 top-full mt-2 w-64 rounded-2xl overflow-hidden"
                        >
                          <div className={`${darkMode 
                            ? 'dark-surface backdrop-blur-xl border dark-border shadow-2xl rounded-2xl p-4'
                            : 'bg-white/90 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl p-4'}`}>
                            <div className={`pb-3 mb-3 ${darkMode ? 'border-b dark-border' : 'border-b border-gray-100'}`}>
                              <p className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{user?.email}</p>
                              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Administrator</p>
                            </div>
                            <a
                              href="/"
                              className={`flex items-center space-x-3 p-2 rounded-lg transition-all ${darkMode ? 'dark-hover' : 'hover:bg-gray-50'}`}
                              onClick={() => setProfileOpen(false)}
                            >
                              <FaHome className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                              <span className={`text-sm ${darkMode ? 'dark-text' : 'text-gray-700'}`}>View Site</span>
                            </a>
                            <button
                              onClick={() => setActiveTab('settings')}
                              className={`w-full flex items-center space-x-3 p-2 rounded-lg transition-all text-left ${darkMode ? 'dark-hover' : 'hover:bg-gray-50'}`}
                            >
                              <FaCog className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                              <span className={`text-sm ${darkMode ? 'dark-text' : 'text-gray-700'}`}>Settings</span>
                            </button>
                            <button
                              onClick={handleSignOut}
                              className={`w-full flex items-center space-x-3 p-2 rounded-lg transition-all text-left mt-2 pt-3 ${
                                darkMode 
                                  ? 'hover:bg-red-900/20 border-t dark-border' 
                                  : 'hover:bg-red-50 border-t border-gray-100'
                              }`}
                            >
                              <FaSignOutAlt className="w-4 h-4 text-red-600" />
                              <span className="text-sm text-red-600 font-medium">Sign Out</span>
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <a href="/" className="flex items-center space-x-2">
                <img
                  src="/assets/assets/images/logo.webp"
                  alt="Paradise-Performance"
                  className="h-8"
                />
                <span className="font-bold text-primary-600">Admin</span>
              </a>
              
              <div className="flex items-center space-x-2">
                <motion.button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  whileTap={{ scale: 0.95 }}
                  className="p-2 rounded-lg bg-white/50 backdrop-blur cursor-pointer active:bg-white/70 transition-colors"
                  aria-label="Toggle mobile menu"
                >
                  {mobileMenuOpen ? 
                    <FiX className="w-6 h-6 text-gray-700" /> : 
                    <FiMenu className="w-6 h-6 text-gray-700" />
                  }
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        {/* Search Overlay */}
        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`${darkMode 
                ? 'dark-surface backdrop-blur-xl border-b dark-border'
                : 'bg-white/90 backdrop-blur-xl border-b border-white/20'}`}
            >
              <div className="px-6 py-4">
                <div className="max-w-2xl mx-auto relative">
                  <FaSearch className="absolute left-4 top-3.5 text-gray-400" />
                  <input
                    type="text"
                    autoFocus
                    placeholder="Search navigation..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`w-full pl-12 pr-4 py-3 rounded-xl backdrop-blur focus:outline-none focus:ring-2 focus:ring-primary-500/50 ${
                      darkMode
                        ? 'bg-white/10 border dark-border text-gray-100 placeholder-gray-500'
                        : 'bg-white/50 border border-white/20'
                    }`}
                  />
                  <button
                    onClick={() => {
                      setSearchOpen(false);
                      setSearchQuery('');
                    }}
                    className="absolute right-4 top-3.5 text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              className="fixed inset-0 z-[9999] lg:hidden"
            >
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
              <motion.div className={`absolute right-0 top-0 bottom-0 w-[85%] max-w-sm backdrop-blur-xl shadow-2xl overflow-y-auto ${
                darkMode ? 'bg-slate-900/95' : 'bg-white/95'
              }`}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className={`text-xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Navigation</h2>
                    <button
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-lg hover:bg-gray-100"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <nav className="space-y-2">
                    {navItems.map((item) => (
                      <div key={item.component}>
                        <button
                          onClick={() => {
                            if (!item.dropdown) {
                              setActiveTab(item.component);
                              setMobileMenuOpen(false);
                            } else {
                              setExpandedDropdown(expandedDropdown === item.component ? null : item.component);
                            }
                          }}
                          className={`
                            w-full flex items-center justify-between p-3 rounded-xl
                            transition-all duration-200
                            ${activeTab === item.component 
                              ? 'bg-gradient-to-r ' + item.color + ' text-white' 
                              : darkMode ? 'hover:bg-slate-800 text-gray-200' : 'hover:bg-gray-50 text-gray-700'
                            }
                          `}
                        >
                          <span className="flex items-center space-x-3">
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.name}</span>
                          </span>
                          {item.dropdown && (
                            <FiChevronDown className={`w-4 h-4 transition-transform ${expandedDropdown === item.component ? 'rotate-180' : ''}`} />
                          )}
                        </button>
                        
                        {item.dropdown && expandedDropdown === item.component && (
                          <div className="ml-8 mt-2 space-y-1">
                            {item.dropdown.map((dropItem) => (
                              <button
                                key={dropItem.component}
                                onClick={() => {
                                  setActiveTab(dropItem.component);
                                  setMobileMenuOpen(false);
                                }}
                                className={`
                                  w-full text-left p-2 rounded-lg text-sm
                                  ${activeTab === dropItem.component 
                                    ? darkMode ? 'bg-primary-900/30 text-primary-400' : 'bg-primary-50 text-primary-600' 
                                    : darkMode ? 'hover:bg-slate-800 text-gray-400' : 'hover:bg-gray-50 text-gray-600'
                                  }
                                `}
                              >
                                {dropItem.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </nav>
                  
                  {/* Mobile User Profile Section */}
                  <div className="mt-8 pt-8 border-t border-gray-200 dark:border-slate-700">
                    <div className="space-y-3">
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
                        <div className="flex items-center space-x-3 mb-3">
                          <FaUser className="w-8 h-8 text-primary-600" />
                          <div>
                            <p className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                              {user?.email || 'Admin User'}
                            </p>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Administrator
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => {
                          setActiveTab('settings');
                          setMobileMenuOpen(false);
                        }}
                        className={`w-full p-3 rounded-lg text-left flex items-center space-x-3 ${
                          darkMode ? 'hover:bg-slate-800 text-gray-200' : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <FaCog className="w-5 h-5" />
                        <span>Settings</span>
                      </button>
                      
                      <button
                        onClick={() => setDarkMode(!darkMode)}
                        className={`w-full p-3 rounded-lg text-left flex items-center justify-between ${
                          darkMode ? 'hover:bg-slate-800 text-gray-200' : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <span className="flex items-center space-x-3">
                          {darkMode ? <FaMoon className="w-5 h-5" /> : <FaSun className="w-5 h-5" />}
                          <span>Dark Mode</span>
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          darkMode ? 'bg-primary-900/30 text-primary-400' : 'bg-primary-50 text-primary-600'
                        }`}>
                          {darkMode ? 'ON' : 'OFF'}
                        </span>
                      </button>
                      
                      <button
                        onClick={handleSignOut}
                        className="w-full p-3 rounded-lg text-left flex items-center space-x-3 text-red-600 hover:bg-red-50"
                      >
                        <FaSignOutAlt className="w-5 h-5" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
};

export default AdminNavigationModern;
